import { GeneralCall, GetEvmClientFunction } from '../types'
import lodash from 'lodash'
import { createRawRpcCalls, PreparedCall } from './rpcCall'
import { parseRawRpcBatchResponses, RawRpcResponse } from './rpcParse'
import { MULTICALL_DEFAULT_BATCH_SIZE } from './constants'

// pull the functions you need
const { isArray } = lodash

export type Call = GeneralCall

const maxRetries = 3

/**
 * Sentinel written into the result array for a call that returned NO data —
 * a revert, an RPC error, or a whole `aggregate3` chunk that was rejected
 * (rate limit, subrequest cap, dropped connection: viem marks every call in a
 * rejected chunk as `status: 'failure'`).
 *
 * It is NOT a zero value. Parsers must skip these slots — coercing one to `0`
 * turns a failed read into a phantom "no balance" position and, worse, makes a
 * real deposit or debt silently disappear from a user's portfolio.
 */
export const MULTICALL_FAILURE = '0x'

/** True when a multicall slot holds no usable data (see {@link MULTICALL_FAILURE}). */
export const isFailedCall = (value: unknown): boolean =>
  value === undefined || value === null || value === MULTICALL_FAILURE

/**
 * How many EXTRA endpoints a failed slot is re-tried on before it is given up
 * on. Each round moves to the next rpcId, so this is "how many independent
 * endpoints must all fail before we report no data".
 */
export const MULTICALL_REPAIR_ROUNDS = 3

/** How many calls at most go into a single `aggregate3` REQUEST.
 *
 * This is the only size knob that matters. Measured on Ethereum (3,086
 * user-data calls, the three production endpoints): a single request carrying
 * 400 calls answers in 320–590ms and 1,600 in 1.0–2.7s, so the round-trip cost
 * is dominated by the request, not the calls inside it. Above ~800 the
 * endpoints start refusing wholesale (response/gas caps) and they disagree
 * about where — one production gateway fails at 800 while another serves 1,600
 * — so this sits below the lowest observed ceiling and
 * {@link endpointCallCap} walks it down further per endpoint when one still
 * refuses.
 */
const MAX_CALLS_PER_SHARD = 300

/**
 * Byte budget passed to viem so that ONE shard is ONE request.
 *
 * `batchSize` is viem's per-request calldata BYTE limit, and it was the real
 * cause of the fan-out this module used to produce: at the 4096-byte default a
 * 400-call shard is re-split into ~10 `aggregate3` requests that viem fires
 * CONCURRENTLY at a single endpoint. Three shards in flight then meant ~30
 * simultaneous requests — which a Cloudflare Worker cannot even issue (it gets
 * six connections waiting for response headers; the seventh queues) and which
 * the endpoints answer by rate-limiting. Measured: the same 3,086 calls took
 * 10.0s that way and 1.4s as whole-shard requests on one endpoint.
 *
 * Sizing is therefore done in CALLS ({@link MAX_CALLS_PER_SHARD}) and the byte
 * limit is set out of the way.
 */
const ONE_REQUEST_BATCH_BYTES = 1_000_000

/**
 * Total requests this module keeps in flight, across every shard and hedge.
 *
 * Cloudflare Workers allow six connections to be simultaneously waiting for
 * response headers per invocation and QUEUE the seventh, so fanning out past
 * this buys nothing at the edge and costs head-of-line blocking: a queued
 * request cannot start until an earlier one — possibly a stalled one — returns
 * headers.
 */
const MAX_INFLIGHT_REQUESTS = 6

/**
 * How long a shard may go unanswered before the SAME shard is re-issued on a
 * second endpoint, first answer wins.
 *
 * This is the single biggest win available here, because the problem is tail
 * latency rather than throughput. Measured per-endpoint on Ethereum: healthy
 * whole-shard requests answer in 200–600ms, but any endpoint will occasionally
 * stall to viem's timeout, and one stall used to set the response time for the
 * whole chain. Hedging at 1.2s cut a 3,086-call read from 10.0s to 3.2–3.8s
 * AND removed the failed slots (316 → 157, the permanent-revert floor), so the
 * repair pass below stops firing at all.
 *
 * The cost is a duplicated request for every shard slower than the delay —
 * keep the delay well above the healthy latency so that stays rare.
 */
const HEDGE_DELAY_MS = 1200

/**
 * Per-request timeout asked of the client factory (viem defaults to 10s).
 *
 * Long enough for the largest shard on a slow-but-working endpoint, short
 * enough that a dead one is detected and re-issued rather than waited out. A
 * factory that ignores the option just keeps viem's default; hedging covers
 * that case anyway.
 */
const SHARD_TIMEOUT_MS = 4000

/**
 * Smallest a learned cap may shrink to.
 *
 * Every failure this module can observe looks the same — a refusal carries no
 * reason — so a run of rate limits or 502s is indistinguishable from a real
 * response-size ceiling and would otherwise halve the cap down to single-call
 * requests, i.e. turn one round trip into hundreds. The floor bounds how wrong
 * that inference can make us.
 */
const MIN_ADAPTIVE_SHARD = 25

/**
 * How long a learned cap is trusted before the endpoint is retried at full
 * size.
 *
 * A ceiling is a property of the endpoint's CURRENT load as much as its
 * config, so a cap must expire or a single bad minute permanently shrinks an
 * endpoint that recovered seconds later.
 */
const ADAPTIVE_CAP_TTL_MS = 60_000

/**
 * Largest call count each endpoint URL has been seen to REFUSE wholesale,
 * halved into a working cap.
 *
 * Endpoints disagree about their own limits and publish none of them — one
 * production gateway refuses 800 calls while another serves 1,600 — so the only
 * way to learn is to be refused. Kept per process (a Worker isolate): cheap,
 * self-correcting, and deliberately not persisted.
 */
const endpointCallCap = new Map<string, { calls: number; at: number }>()

const capFor = (url: string, requested: number): number => {
  const hit = endpointCallCap.get(url)
  if (!hit) return requested
  if (Date.now() - hit.at > ADAPTIVE_CAP_TTL_MS) {
    endpointCallCap.delete(url)
    return requested
  }
  return Math.min(requested, hit.calls)
}

const noteEndpointRefusal = (url: string, calls: number): void => {
  if (calls <= MIN_ADAPTIVE_SHARD) return
  const next = Math.max(MIN_ADAPTIVE_SHARD, Math.floor(calls / 2))
  const current = endpointCallCap.get(url)
  if (
    current &&
    Date.now() - current.at <= ADAPTIVE_CAP_TTL_MS &&
    current.calls <= next
  )
    return
  endpointCallCap.set(url, { calls: next, at: Date.now() })
}

/** Test seam — the cap is process-global, so suites must not inherit each other's. */
export const resetEndpointCallCaps = (): void => {
  endpointCallCap.clear()
}

/** Viem error names that mean the call itself failed deterministically. */
const DETERMINISTIC_ERROR_NAMES = new Set([
  'ContractFunctionRevertedError',
  'RawContractError',
  'AbiDecodingZeroDataError',
  'AbiDecodingDataSizeTooSmallError',
  'AbiFunctionNotFoundError',
])

/**
 * Distinguishes a call that will fail again on every endpoint (a revert, a
 * missing selector, an address with no code) from one that failed because of
 * the TRANSPORT (429, 502, timeout, dropped connection).
 *
 * This matters because viem's `multicall` never throws under `allowFailure`:
 * when a whole `aggregate3` chunk is rejected it marks every call in that chunk
 * `status: 'failure'` and hangs the transport error off each entry. Without
 * this classification the endpoint-failover paths below can't fire at all for
 * the one failure mode they exist for — the request "succeeds" while returning
 * nothing but sentinels.
 *
 * Unknown errors are treated as transient: re-reading is cheap, reporting a
 * position that exists as absent is not.
 */
export const isDeterministicCallError = (error: any): boolean => {
  let err = error
  for (let depth = 0; err && depth < 8; depth++) {
    if (DETERMINISTIC_ERROR_NAMES.has(err.name)) return true
    const message = String(err.shortMessage ?? err.message ?? '').toLowerCase()
    if (
      message.includes('execution reverted') ||
      message.includes('returned no data')
    )
      return true
    err = err.cause
  }
  return false
}

/**
 * Base delay between repair rounds, doubled per round and jittered.
 *
 * A rate limit is a statement about TIME: re-reading the instant a 429 lands
 * just spends the next endpoint's budget inside the same window, which is how a
 * burst takes out an entire pool at once. The pause is small enough to stay
 * inside a user-facing request (~0.1s + 0.2s + 0.4s worst case) and is only ever
 * paid on a read that already failed.
 */
const REPAIR_BACKOFF_BASE_MS = 100
const REPAIR_BACKOFF_JITTER_MS = 60

const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, Math.max(0, ms)))

const backoffForRound = (round: number) =>
  REPAIR_BACKOFF_BASE_MS * 2 ** round + Math.random() * REPAIR_BACKOFF_JITTER_MS

/** Reported for each endpoint that failed to serve a batch. */
export interface EndpointFailure {
  chainId: string
  /** Endpoint URL, or `rpc#<id>` when the transport does not expose one. */
  url: string
  rpcId: number
  /** `transport` — the request itself died. `slots` — it answered with nothing but failures. */
  kind: 'transport' | 'slots'
}

export interface MulticallEndpointOptions {
  /**
   * Endpoint URLs already attempted for this call set. Failover consults it so
   * a retry lands on an endpoint that has NOT already failed — see
   * {@link resolveEndpoint}.
   */
  tried?: Set<string>
  /** Invoked for every endpoint that fails, so callers can demote it. */
  onEndpointFailure?: (info: EndpointFailure) => void
}

const endpointUrl = (client: any, rpcId: number): string =>
  client?.transport?.url ?? `rpc#${rpcId}`

/**
 * Picks the endpoint for an attempt, PREFERRING one not yet tried for this call
 * set.
 *
 * `rpcId + 1` is NOT a different endpoint. The provider factory maps ids onto a
 * finite list — the custom-RPC path WRAPS (`rpcId % list.length`) and the
 * default path CLAMPS (`min(list.length - 1, rpcId)`) — so with a 3-endpoint
 * pool the third failover round lands right back on the endpoint that just
 * rate-limited us, and past the end every further "failover" re-hits the same
 * last endpoint forever. Both look like retries and neither is one. Resolving by
 * URL rather than by index handles wrap and clamp identically, with no knowledge
 * of the pool size.
 *
 * Preference, not prohibition: once every endpoint has been tried it falls back
 * to reusing one (flagged `reused`). A chain configured with a single RPC must
 * still get its retries — the failures this exists for are transient, so the
 * same endpoint a moment later is a genuine second chance, just a worse one than
 * a fresh endpoint would have been. Returns `null` only when no endpoint can be
 * constructed at all.
 */
const resolveEndpoint = (
  chainId: string,
  getEvmClient: GetEvmClientFunction,
  startRpcId: number,
  tried: Set<string> | undefined,
  maxProbe = 12,
  timeoutMs?: number,
): { client: any; rpcId: number; url: string; reused: boolean } | null => {
  let fallback: {
    client: any
    rpcId: number
    url: string
    reused: boolean
  } | null = null

  for (let probe = 0; probe < maxProbe; probe++) {
    const rpcId = startRpcId + probe
    let client: any
    try {
      client = getEvmClient(chainId, rpcId, { timeoutMs })
    } catch {
      break // pool exhausted / misconfigured
    }
    const url = endpointUrl(client, rpcId)
    if (!tried?.has(url)) {
      tried?.add(url)
      return { client, rpcId, url, reused: false }
    }
    // Remember the first candidate in case every one of them is already tried.
    fallback ??= { client, rpcId, url, reused: true }
  }
  return fallback
}

/** A result slot plus whether re-reading it could ever produce data. */
interface Slot {
  value: any
  /** Deterministic failure — retrying on another endpoint is pointless. */
  permanent: boolean
}

const toSlots = (returnData: any[], allowFailure: boolean): Slot[] => {
  if (!allowFailure)
    return returnData.map((value) => ({ value, permanent: false }))
  return (returnData as any[]).map(({ result, status, error }: any) =>
    status === 'success'
      ? { value: result, permanent: false }
      : {
          value: MULTICALL_FAILURE,
          permanent: isDeterministicCallError(error),
        },
  )
}

const slotValues = (slots: Slot[]): any[] => slots.map((s) => s.value)

/**
 * Records which result indices failed DETERMINISTICALLY into the caller's
 * collector.
 *
 * The sentinel itself cannot carry this: dozens of parsers compare raw slots to
 * `'0x'` directly, so a second sentinel value would read as data in every one
 * of them. A caller that wants to re-read failures needs the distinction —
 * without it, a market that always reverts (no position, unsupported selector)
 * looks exactly like a rate-limited read and gets re-fetched on every request,
 * forever.
 */
const recordPermanentFailures = (
  slots: Slot[],
  offset: number,
  collector: Set<number> | undefined,
): void => {
  if (!collector) return
  for (let i = 0; i < slots.length; i++) {
    if (slots[i].permanent) collector.add(offset + i)
  }
}

/**
 * Re-runs the slots that came back as failures on successive endpoints.
 *
 * Each round waits out a jittered backoff and moves to an endpoint that has not
 * already failed for this call set, falling back to reuse once they all have
 * (see {@link resolveEndpoint}). The loop stops as soon as every slot holds
 * data. Slots whose error was deterministic (see
 * {@link isDeterministicCallError}) are excluded — they would burn a round to
 * fail identically. Mutates and returns `slots`; never throws, so a repair pass
 * that dies just leaves the remaining sentinels in place.
 */
const repairFailedSlots = async (
  chainId: string,
  contracts: any[],
  slots: Slot[],
  getEvmClient: GetEvmClientFunction,
  nextRpcId: number,
  batchSize: number,
  logs: boolean,
  rounds = MULTICALL_REPAIR_ROUNDS,
  options?: MulticallEndpointOptions,
): Promise<Slot[]> => {
  let searchFrom = nextRpcId
  for (let round = 0; round < rounds; round++) {
    const failedIdx: number[] = []
    for (let i = 0; i < slots.length; i++) {
      if (isFailedCall(slots[i].value) && !slots[i].permanent) failedIdx.push(i)
    }
    if (failedIdx.length === 0) return slots

    const endpoint = resolveEndpoint(
      chainId,
      getEvmClient,
      searchFrom,
      options?.tried,
    )
    if (!endpoint) {
      if (logs)
        console.log(
          `multicall repair: no usable endpoint for chain ${chainId}, giving up with ${failedIdx.length} failed slots`,
        )
      return slots
    }
    searchFrom = endpoint.rpcId + 1

    await sleep(backoffForRound(round))

    try {
      const returnData = await endpoint.client.multicall({
        allowFailure: true,
        batchSize,
        contracts: failedIdx.map((i) => contracts[i]),
      })
      let repaired = 0
      ;(returnData as any[]).forEach((entry: any, k: number) => {
        const idx = failedIdx[k]
        if (entry?.status === 'success') {
          slots[idx] = { value: entry.result, permanent: false }
          repaired++
        } else if (isDeterministicCallError(entry?.error)) {
          slots[idx].permanent = true
        }
      })
      if (repaired === 0)
        options?.onEndpointFailure?.({
          chainId,
          url: endpoint.url,
          rpcId: endpoint.rpcId,
          kind: 'slots',
        })
    } catch (error: any) {
      options?.onEndpointFailure?.({
        chainId,
        url: endpoint.url,
        rpcId: endpoint.rpcId,
        kind: 'transport',
      })
      if (logs)
        console.log(
          `error in multicall failure-repair round ${round} chain ${chainId}`,
          error,
        )
    }
  }
  return slots
}

/**
 * Same as Multicall viem except that the abi param is supposed to be the abi per call
 *
 * `retryFailed` re-reads the slots that came back as failures on successive
 * endpoints (see {@link repairFailedSlots}) — worth it for reads whose consumer
 * cannot tell "call failed" from "value is zero".
 */
export const multicallViemAbiArray = async (
  chainId: string,
  abi: any[],
  calls: Call[],
  getEvmClient: GetEvmClientFunction,
  retry = true,
  providerId = 0,
  retries = maxRetries,
  allowFailure = true,
  batchSize = MULTICALL_DEFAULT_BATCH_SIZE,
  logs = false,
  retryFailed = false,
  permanentFailures?: Set<number>,
  options?: MulticallEndpointOptions,
): Promise<any[]> => {
  // Endpoints already burned on this call set, so failover can pick one that
  // has not — `providerId + 1` alone does not guarantee a different endpoint.
  const tried = options?.tried ?? new Set<string>()
  const endpoint = resolveEndpoint(chainId, getEvmClient, providerId, tried)
  if (!endpoint) throw new Error('No usable RPC endpoint for ' + chainId)
  const endpointOptions: MulticallEndpointOptions = {
    tried,
    onEndpointFailure: options?.onEndpointFailure,
  }

  try {
    const abiIsArray = isArray(abi[0])
    const contracts = calls.map(({ address, name, params }, i) => ({
      abi: abiIsArray ? (abi?.[i] as any) : abi,
      address: address as any,
      functionName: name,
      args: params,
    }))
    const returnData = await endpoint.client.multicall({
      allowFailure,
      batchSize,
      contracts,
    })

    // no failure - result is directly exposed
    if (!allowFailure) return returnData

    // allow failure, return the failure sentinel in case of errors
    const slots = toSlots(returnData as any[], true)

    // Every slot failed transiently: that is an endpoint-level failure viem
    // swallowed instead of throwing, so treat it exactly like a thrown one and
    // move to the next endpoint rather than handing back a batch of sentinels.
    if (
      retries > 0 &&
      slots.length > 0 &&
      slots.every((s) => isFailedCall(s.value) && !s.permanent)
    ) {
      if (logs)
        console.log(
          `multicall fully failed on rpc ${endpoint.rpcId} for chain ${chainId}, failing over`,
        )
      options?.onEndpointFailure?.({
        chainId,
        url: endpoint.url,
        rpcId: endpoint.rpcId,
        kind: 'slots',
      })
      await sleep(backoffForRound(0))
      return multicallViemAbiArray(
        chainId,
        abi,
        calls,
        getEvmClient,
        retry,
        endpoint.rpcId + 1,
        retries - 1,
        allowFailure,
        batchSize,
        logs,
        retryFailed,
        permanentFailures,
        endpointOptions,
      )
    }

    const finalSlots = retryFailed
      ? await repairFailedSlots(
          chainId,
          contracts,
          slots,
          getEvmClient,
          endpoint.rpcId + 1,
          batchSize,
          logs,
          MULTICALL_REPAIR_ROUNDS,
          endpointOptions,
        )
      : slots
    recordPermanentFailures(finalSlots, 0, permanentFailures)
    return slotValues(finalSlots)
  } catch (error: any) {
    // optionally log errors
    if (logs) console.log('error in multicall', error)
    options?.onEndpointFailure?.({
      chainId,
      url: endpoint.url,
      rpcId: endpoint.rpcId,
      kind: 'transport',
    })
    retries--
    if (retries < 0) throw new Error('Failed too often')

    await sleep(backoffForRound(0))
    return multicallViemAbiArray(
      chainId,
      abi,
      calls,
      getEvmClient,
      retry,
      endpoint.rpcId + 1,
      retries,
      allowFailure,
      batchSize,
      logs,
      retryFailed,
      permanentFailures,
      endpointOptions,
    )
  }
}

/**
 * Sharded variant of {@link multicallViemAbiArray}: splits the flat call set
 * into shards of at most {@link MAX_CALLS_PER_SHARD} calls, sends each as ONE
 * `aggregate3` request, and spreads them across distinct RPC endpoints — one
 * request in flight per endpoint, plus at most one hedge.
 *
 * **The unit that matters is CALLS PER REQUEST, not endpoints.** Fanning the
 * same work across more endpoints does not make it faster; issuing it as fewer,
 * larger requests does. Measured on Ethereum with 3,086 user-data calls against
 * the three production endpoints:
 *
 * | shape                                          | wall  | failed slots |
 * |------------------------------------------------|-------|--------------|
 * | 3 endpoints, 4096-byte chunks (the old default) | 10.0s | 316          |
 * | 1 endpoint, whole shards                        | 1.4s  | 157          |
 * | 3 endpoints, whole shards, hedged at 1.2s       | 3.2s  | 157          |
 *
 * (157 is the permanent-revert floor — calls that fail identically everywhere.)
 * The old shape lost because `batchSize` re-split every shard into ~10
 * concurrent requests per endpoint: ~30 in flight, which a Worker cannot even
 * issue (six connections may wait for headers; the seventh queues) and which
 * endpoints answer by rate-limiting. Concurrency per endpoint is actively
 * harmful — measured, four concurrent 200-call requests to one endpoint took
 * 1.5s against 281ms for one, and eight timed out.
 *
 * `batchSize` is therefore IGNORED here unless a caller passes something
 * smaller than {@link MULTICALL_DEFAULT_BATCH_SIZE}, which is read as a
 * deliberate "this endpoint needs small responses" and honoured. Sizing is done
 * in calls, adaptively: an endpoint that refuses a shard wholesale has its cap
 * halved for the rest of the process ({@link endpointCallCap}), because the
 * endpoints disagree about their own ceilings and publish none of them.
 *
 * Failure handling, in order of cost:
 *  1. **Hedge** — a shard unanswered after {@link HEDGE_DELAY_MS}, or one whose
 *     endpoint failed outright, is re-issued on another endpoint immediately;
 *     first usable answer wins. This is what collapses the tail.
 *  2. **Split** — a shard both endpoints refuse is halved and retried, which
 *     walks past a response-size or gas ceiling.
 *  3. **Repair** — with `retryFailed`, individual failed slots are re-read on
 *     further endpoints ({@link MULTICALL_REPAIR_ROUNDS}); reverted slots are
 *     excluded and reported via `permanentFailures`.
 *
 * A shard that survives all three leaves its slots undefined rather than
 * throwing, so one dead range cannot discard the shards that succeeded —
 * `isFailedCall` reads that as "no data", which is the truth. Results are
 * always reassembled in the original call order.
 */
export const multicallShardedAbiArray = async (
  chainId: string,
  abi: any[],
  calls: Call[],
  getEvmClient: GetEvmClientFunction,
  poolSize: number,
  retries = maxRetries,
  allowFailure = true,
  batchSize = MULTICALL_DEFAULT_BATCH_SIZE,
  logs = false,
  retryFailed = false,
  permanentFailures?: Set<number>,
  options?: MulticallEndpointOptions,
): Promise<any[]> => {
  // A single-endpoint pool STILL goes through the sharded machinery — with one
  // worker it degrades to sequential whole-shard requests on that endpoint.
  // The old special case delegated to `multicallViemAbiArray` with the default
  // 4,096-BYTE viem batch, which re-split the call set into ~16-call requests
  // fired concurrently: measured on Ethereum, 72 requests instead of 5 — a 14×
  // request bill exactly on the chains that can least afford it (one endpoint
  // is usually one FREE endpoint). Delegating with a raised byte limit is not
  // the fix either: one request carrying every call blows past the ~800-call
  // refusal ceiling some gateways enforce, and the unsharded path has no
  // split-on-refusal to recover with. Sharding by calls, sequentially, is.

  const abiIsArray = isArray(abi[0])
  const contracts = calls.map(({ address, name, params }, i) => ({
    abi: abiIsArray ? (abi?.[i] as any) : abi,
    address: address as any,
    functionName: name,
    args: params,
  }))

  // A caller asking for LESS than the default is asking for small responses on
  // purpose (an endpoint with a tight response cap) — honour it. Anything else
  // is the default nobody chose, and chunking by it is the bug this function
  // exists to avoid, so push the byte limit out of the way and size in calls.
  const requestBytes =
    batchSize < MULTICALL_DEFAULT_BATCH_SIZE
      ? batchSize
      : ONE_REQUEST_BATCH_BYTES

  // Each worker owns one endpoint and may hold one hedge open against another,
  // so it costs two of the six connection slots a Worker invocation gets.
  const workerBudget = Math.max(1, Math.floor(MAX_INFLIGHT_REQUESTS / 2))
  const workers = Math.max(1, Math.min(poolSize, workerBudget))

  // Enough shards to keep every worker fed, each below the per-request ceiling.
  const shardSize = Math.max(
    1,
    Math.min(MAX_CALLS_PER_SHARD, Math.ceil(contracts.length / workers)),
  )
  const batches: { start: number; items: typeof contracts }[] = []
  for (let i = 0; i < contracts.length; i += shardSize) {
    batches.push({ start: i, items: contracts.slice(i, i + shardSize) })
  }

  const results: any[] = new Array(contracts.length)

  type Endpoint = NonNullable<ReturnType<typeof resolveEndpoint>>
  type Won = { slots: Slot[]; rpcId: number }

  /**
   * One `aggregate3` request. Resolves to the slots, or to `null` when the
   * endpoint produced nothing usable — whether it THREW or answered with
   * nothing but transient failures, which is what viem reports for a rejected
   * chunk and which keying failover on thrown errors alone used to miss.
   */
  const request = async (
    endpoint: Endpoint,
    items: typeof contracts,
  ): Promise<Slot[] | null> => {
    try {
      const returnData = await endpoint.client.multicall({
        allowFailure,
        batchSize: requestBytes,
        contracts: items,
      })
      const slots = toSlots(returnData as any[], allowFailure)
      if (
        allowFailure &&
        slots.length > 0 &&
        slots.every((s) => isFailedCall(s.value) && !s.permanent)
      ) {
        if (logs)
          console.log(
            `sharded multicall: rpc ${endpoint.rpcId} answered ${items.length} calls with nothing`,
          )
        options?.onEndpointFailure?.({
          chainId,
          url: endpoint.url,
          rpcId: endpoint.rpcId,
          kind: 'slots',
        })
        noteEndpointRefusal(endpoint.url, items.length)
        return null
      }
      return slots
    } catch (error: any) {
      if (logs) console.log('error in sharded multicall request', error)
      options?.onEndpointFailure?.({
        chainId,
        url: endpoint.url,
        rpcId: endpoint.rpcId,
        kind: 'transport',
      })
      noteEndpointRefusal(endpoint.url, items.length)
      return null
    }
  }

  /** Resolves with the first non-null result, or null once all have resolved. */
  const firstUsable = (attempts: Promise<Won | null>[]): Promise<Won | null> =>
    new Promise((resolve) => {
      let pending = attempts.length
      let settled = false
      const lose = () => {
        if (!settled && --pending === 0) resolve(null)
      }
      for (const attempt of attempts) {
        attempt.then((won) => {
          if (settled) return
          if (won) {
            settled = true
            resolve(won)
          } else lose()
        }, lose)
      }
    })

  /**
   * Sends a shard to one endpoint and, if it has not answered by
   * {@link HEDGE_DELAY_MS} *or* has failed outright, to a second one — whichever
   * answers first wins. The failure trigger matters as much as the timer: a
   * fast rejection should fail over immediately rather than wait out a delay
   * sized for a slow success.
   */
  const attemptHedged = async (
    items: typeof contracts,
    startRpcId: number,
    tried: Set<string>,
  ): Promise<Won | null> => {
    const primary = resolveEndpoint(
      chainId,
      getEvmClient,
      startRpcId,
      tried,
      12,
      SHARD_TIMEOUT_MS,
    )
    if (!primary) {
      if (logs)
        console.log(
          `sharded multicall: no untried endpoint left for chain ${chainId}`,
        )
      return null
    }

    let timer: any
    let hedgeStarted = false
    let beginHedge = () => {}
    const hedged = new Promise<Won | null>((resolve) => {
      beginHedge = () => {
        if (hedgeStarted) return
        hedgeStarted = true
        clearTimeout(timer)
        const alt = resolveEndpoint(
          chainId,
          getEvmClient,
          primary.rpcId + 1,
          tried,
          12,
          SHARD_TIMEOUT_MS,
        )
        if (!alt) return resolve(null)
        const altItems = items.slice(0, capFor(alt.url, items.length))
        // A narrowed cap would answer only part of the shard — not a usable
        // substitute for the whole, so let the split path handle it instead.
        if (altItems.length < items.length) return resolve(null)
        request(alt, items).then((slots) =>
          resolve(slots ? { slots, rpcId: alt.rpcId } : null),
        )
      }
    })
    timer = setTimeout(() => beginHedge(), HEDGE_DELAY_MS)

    const direct = request(primary, items).then((slots) => {
      if (slots) return { slots, rpcId: primary.rpcId }
      beginHedge()
      return null
    })

    const won = await firstUsable([direct, hedged])
    clearTimeout(timer)
    return won
  }

  const runBatch = async (
    batch: { start: number; items: typeof contracts },
    rpcId: number,
    attemptsLeft: number,
    // Per-shard, so shards still spread across the pool as intended while each
    // one's own failover never returns to an endpoint that already failed IT.
    tried: Set<string>,
  ): Promise<void> => {
    if (!batch.items.length) return

    const won = await attemptHedged(batch.items, rpcId, tried)

    if (!won) {
      // Both endpoints refused the shard. Halving is the move that gets past a
      // response-size or gas ceiling, which is the failure the adaptive cap has
      // just recorded; the two halves reuse `tried` so they keep rotating.
      if (attemptsLeft > 0 && batch.items.length > 1) {
        const half = Math.ceil(batch.items.length / 2)
        await sleep(backoffForRound(0))
        await runBatch(
          { start: batch.start, items: batch.items.slice(0, half) },
          rpcId,
          attemptsLeft - 1,
          tried,
        )
        await runBatch(
          { start: batch.start + half, items: batch.items.slice(half) },
          rpcId + 1,
          attemptsLeft - 1,
          tried,
        )
      }
      // Otherwise leave the slots undefined — `isFailedCall` reads that as "no
      // data", which is the truth, rather than discarding sibling shards.
      return
    }

    let slots = won.slots
    if (allowFailure && retryFailed) {
      slots = await repairFailedSlots(
        chainId,
        batch.items,
        slots,
        getEvmClient,
        won.rpcId + 1,
        requestBytes,
        logs,
        MULTICALL_REPAIR_ROUNDS,
        { tried, onEndpointFailure: options?.onEndpointFailure },
      )
    }

    recordPermanentFailures(slots, batch.start, permanentFailures)
    for (let j = 0; j < slots.length; j++) {
      results[batch.start + j] = slots[j].value
    }
  }

  // Worker pool: each worker pulls shards off a shared cursor and starts from
  // its own endpoint, so every RPC sees ONE request at a time (measured: four
  // concurrent requests to one endpoint are 5x slower than one, eight time out).
  let cursor = 0
  const worker = async (workerId: number): Promise<void> => {
    while (true) {
      const idx = cursor++
      if (idx >= batches.length) break
      const batch = batches[idx]
      // Fresh per shard: the exclusion set exists to stop THIS shard from
      // retrying an endpoint that already failed it, not to stop other shards
      // from using an endpoint that is working for them.
      const tried = new Set<string>()
      // Respect what this worker's endpoint has been seen to refuse, splitting
      // the shard up front rather than paying a rejection to rediscover it.
      const start = resolveEndpoint(
        chainId,
        getEvmClient,
        workerId,
        undefined,
        12,
        SHARD_TIMEOUT_MS,
      )
      const cap = start
        ? capFor(start.url, batch.items.length)
        : batch.items.length
      for (let off = 0; off < batch.items.length; off += cap) {
        await runBatch(
          {
            start: batch.start + off,
            items: batch.items.slice(off, off + cap),
          },
          workerId,
          retries,
          tried,
        )
      }
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(workers, batches.length) }, (_, w) =>
      worker(w),
    ),
  )

  return results
}

export interface MulticallConfig {
  chainId: string
  allowFailure: boolean
  batchSize: number
  retries: number
  providerId: number
  logs: boolean
}

export function prepareMulticallInputs(
  abi: any[],
  calls: Call[],
): PreparedCall[] {
  const abiIsArray = isArray(abi[0])

  return calls.map(({ address, name, params }, i) => ({
    address,
    functionName: name,
    params: params || [],
    abi: abiIsArray ? (abi as any[])[i] : (abi as any),
  }))
}

export const executeMulticallViem = async (
  preparedCalls: PreparedCall[],
  config: MulticallConfig,
  getEvmClient: GetEvmClientFunction,
): Promise<any[]> => {
  const abis = preparedCalls.map((c) => c.abi)
  const calls: Call[] = preparedCalls.map((c) => ({
    address: c.address,
    name: c.functionName,
    params: c.params,
  }))

  return multicallViemAbiArray(
    config.chainId,
    abis,
    calls,
    getEvmClient,
    true,
    config.providerId,
    config.retries,
    config.allowFailure,
    config.batchSize,
    config.logs,
  )
}

// // Example: Execute raw RPC calls manually
// async function executeRawMulticall(
//   preparedCalls: PreparedCall[],
//   rpcUrl: string,
//   batchSize: number = MULTICALL_DEFAULT_BATCH_SIZE,
// ) {
//   // 1. Create raw RPC batches
//   const batches = createRawRpcCalls(preparedCalls, batchSize)

//   // 2. Execute batches via fetch
//   const batchResponses: RawRpcResponse[][] = []

//   for (const batch of batches) {
//     const response = await fetch(rpcUrl, {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify(batch.calls),
//     })

//     const results = await response.json()
//     batchResponses.push(results)
//   }

//   // 3. Parse responses
//   return parseRawRpcBatchResponses(batches, batchResponses, true)
// }
